// @ts-strict-ignore
import { FileExtension } from '@shared/util/file-utils';
import * as Pako from 'pako';
import { fromByteArray, toByteArray } from 'base64-js';

type Nullable<T> = T | null | undefined;

export class StringUtils {
  public static readonly DISALLOWED_PROJECT_NAME_FULL_CHARS_REGEXP: RegExp = /[~"#%&*:<>?\/\\{|}$\[\]^`\']/gm;
  public static readonly DISALLOWED_PROJECT_NAME_CHARS_REGEXP: RegExp = /[~%{}^`\']/gm;
  public static readonly TAB_AND_NEW_LINE_REGEXP: RegExp = /(\r\n|\n|\r|\t)/gm;

  public static getFileExtension(fileName: string): string {
    if (StringUtils.isEmpty(fileName) || fileName.indexOf('.') === -1) {
      return null;
    }
    return fileName.substring(fileName.lastIndexOf('.'));
  }

  public static isEmpty(object: Nullable<string>): boolean {
    if (!object) {
      return true;
    }
    return object.trim().length === 0;
  }

  public static isNotEmpty(value: Nullable<string>): value is string {
    return !this.isEmpty(value);
  }

  public static defaultValue(object: any, defaultValue: string): string {
    if (object === null) {
      return defaultValue;
    }
    return object.toString();
  }

  public static compressString(input: string): string {
    const compressed = Pako.deflate(input);
    const base64 = fromByteArray(compressed)
    return base64;
  }

  public static decompressString(base64: string): string {
    const compressed = toByteArray(base64);
    const output = Pako.inflate(compressed, {to: 'string'});
    return output;
  }

  /**
   * Checks is the given string is equal to null or undefined.
   */
  public static isNull(value: string) {
    return value === null || value === undefined;
  }

  public static parseBoolean(obj: any): boolean {
    if (obj instanceof Boolean) {
      return obj as boolean;
    }
    if (typeof obj === 'boolean') {
      return obj;
    }
    const s: string = obj as string;
    if (!s) {
      return false;
    }
    return s.toLowerCase() === 'true' || parseInt(s, 10) > 0;
  }

  public static switchColorOfText(htmlText: string, newHexColor: string): string {
    if (!newHexColor || !htmlText || htmlText.indexOf('COLOR') < 0) {
      return htmlText;
    }
    const regExp: RegExp = new RegExp(/COLOR="([^"]*)"/);
    return htmlText.replace(regExp, 'COLOR="' + newHexColor + '"');
  }

  public static replaceAll(str: string, search: string, replacement: string): string {
    const lnRegExp = new RegExp(search, 'g');
    return str.replace(lnRegExp, replacement);
  }

  public static stringCompare(a: string, b: string, caseInsensitive: boolean = false, numeric: boolean = false): number {
    if (a == null && b == null) {
      return 0;
    }

    if (a == null) {
      return 1;
    }

    if (b == null) {
      return -1;
    }

    // Convert to lowercase if we are case insensitive.
    if (caseInsensitive) {
      a = a.toLocaleLowerCase();
      b = b.toLocaleLowerCase();
    }

    let result: number = a.localeCompare(b, undefined, { numeric });

    if (result < -1) {
      result = -1;
    } else if (result > 1) {
      result = 1;
    }

    return result;
  }

  /**
   * Replace closing paragraphs with linebreaks, remove all other HTML tags.
   */
  public static convertFromFlexHtmlToString(html: string): string {
    if (this.isEmpty(html)) {
      return '';
    }
    const lnRegExp: RegExp = new RegExp('</P>', 'g');
    const extractedText: string = html.replace(lnRegExp, '\n');
    const tagRegExp: RegExp = new RegExp('<([^>\\s]{1,50})(\\s[^>]{1,50}){0,20}>', 'g');
    return extractedText.replace(tagRegExp, '');
  }

  public static isEmptyOrLiteralNull(s: string): boolean {
    if (s === 'null') {
      return true;
    }
    return this.isEmpty(s);
  }
  private static convertCrlfToLineFeeds(s: string): string {
    if (!s) {
      return null;
    }
    const crlf: string = String.fromCharCode(13, 10);
    const regEx: RegExp = new RegExp(crlf, 'g');
    return s.replace(regEx, '\n');
  }
  public static getMaxCharsPerLine(s: string): number {
    if (!s) {
      return 0;
    }
    let maxCharsPerLine: number = 0;
    let charsPerLine: number = 0;
    s = this.convertCrlfToLineFeeds(s);
    for (let i: number = 0; i < s.length; i++) {
      if (s.charCodeAt(i) === 10 || s.charCodeAt(i) === 13) {
        if (charsPerLine > maxCharsPerLine) {
          maxCharsPerLine = charsPerLine;
        }
        charsPerLine = 0;
      } else {
        charsPerLine++;
      }
    }
    if (charsPerLine > maxCharsPerLine) {
      maxCharsPerLine = charsPerLine;
    }
    return maxCharsPerLine;
  }

  public static parseNumber(s: string, decimalSeparator: string, thousandsSeparator: string): number {
    if (StringUtils.isEmpty(s)) {
      return 0;
    }
    let regExp: RegExp = new RegExp(this.escapeRegexChars(thousandsSeparator), 'g');
    s = s.replace(regExp, '');
    regExp = new RegExp(this.escapeRegexChars(decimalSeparator), 'g');
    s = s.replace(regExp, '.');
    return Number(s);
  }

  public static escapeRegexChars(s: string): string {
    if (!s) {
      return null;
    }
    return s.replace(new RegExp('([{}()^$&.*?/+|[\\\\]|]|-)', 'g'), '\\$1');
  }

  public static escapeQuerySelectorChars(s: string): string {
    if (!s) {
      return null;
    }
    return s.replace(new RegExp('([!"#$%&\'()*+-,./:;<=>?@[\\]^`{}|~])', 'g'), '\\$1');
  }

  public static getNumLines(s: string): number {
    if (this.isEmpty(s)) {
      return 0;
    }
    let lineCount: number = 0;
    s = this.convertCrlfToLineFeeds(s);
    if (s.length > 0) {
      lineCount++;
    }
    for (let i = 0; i < s.length; i++) {
      if (s.charCodeAt(i) === 10 || s.charCodeAt(i) === 13) {
        lineCount++;
      }
    }
    return lineCount;
  }

  public static isPrintableCharacter(charCode: number): boolean {
    return charCode >= 32 && charCode !== 127;
  }

  public static sanitizeFileName(fileName: string, fileSuffixToAdd: FileExtension = null): string {
    if (!fileName) {
      return null;
    }
    const pattern: RegExp = new RegExp(/[\\"*?%:/<>\t]/, 'g');
    fileName = fileName.replace(pattern, '_');
    if (fileSuffixToAdd) {
      if (fileSuffixToAdd.charAt(0) === '.') {
        fileName += fileSuffixToAdd;
      } else {
        fileName = fileName + '.' + fileSuffixToAdd;
      }
    }
    return fileName;
  }

  public static sanitizeProjectName(name: string, excludeAllSpecialCharacters: boolean = false): string {
    if (!name) {
      return '';
    }
    const specialCharactersRegex = excludeAllSpecialCharacters
      ? StringUtils.DISALLOWED_PROJECT_NAME_FULL_CHARS_REGEXP
      : StringUtils.DISALLOWED_PROJECT_NAME_CHARS_REGEXP;
    return name.replace(StringUtils.TAB_AND_NEW_LINE_REGEXP, '').replace(specialCharactersRegex, '_');
  }

  public static startsWith(s: string, prefix: string): boolean {
    if (!s) {
      return !prefix;
    }
    if (!prefix) {
      return true;
    }
    return s.substr(0, prefix.length) === prefix;
  }

  public static removePrefix(s: string, prefix: string): string {
    if (!s) {
      return null;
    }
    if (this.startsWith(s, prefix)) {
      return s.substr(prefix.length);
    }
    return s;
  }

  public static getFileSuffix(s: string): string {
    if (this.isEmpty(s)) {
      return null;
    }
    const index: number = s.lastIndexOf('.');
    if (index === -1 || s.length - 1 === index) {
      return null;
    }
    return s.substr(index + 1);
  }

  public static getFileNameFromUrl(s: string): string {
    if (this.isEmpty(s)) {
      return null;
    }
    const index: number = s.lastIndexOf('/');
    if (index === s.length - 1) {
      return null;
    }
    if (index === -1) {
      return s;
    }
    return s.substring(index + 1, s.length);
  }

  public static base64ToByteArray(base64: string): number[] {
    if (this.isEmpty(base64)) {
      return null;
    }
    const replaced: string = base64.substring(base64.indexOf(';base64,')).replace(';base64,', ''); // remove start of base64 string
    const decoded: string = window.atob(replaced); // decode base64
    const byteArray: number[] = [];
    for (let i = 0; i < decoded.length; i++) {
      // eslint-disable-next-line no-bitwise
      const int8Value: number = (decoded.charCodeAt(i) << 24) >> 24; // convert from uint8 to int8 value
      byteArray.push(int8Value);
    }
    return byteArray;
  }

  public static trim(str: string): string {
    if (this.isEmpty(str)) {
      return '';
    }
    let startIndex: number = 0;
    while (this.isWhitespace(str.charAt(startIndex))) {
      ++startIndex;
    }
    let endIndex: number = str.length - 1;
    while (this.isWhitespace(str.charAt(endIndex))) {
      --endIndex;
    }
    if (endIndex >= startIndex) {
      return str.slice(startIndex, endIndex + 1);
    } else {
      return '';
    }
  }

  public static isWhitespace(character: string): boolean {
    switch (character) {
      case ' ':
      case '\t':
      case '\r':
      case '\n':
      case '\f':
        return true;

      default:
        return false;
    }
  }

  public static containsString(stringToCheck: string, str: string, caseInsensitive: boolean = true): boolean {
    if (this.isEmpty(stringToCheck) || this.isEmpty(str)) {
      return false;
    }
    if (caseInsensitive) {
      stringToCheck = stringToCheck.toLocaleLowerCase();
      str = str.toLocaleLowerCase();
    }
    return stringToCheck.indexOf(str) !== -1;
  }
  public static containsStrings(stringToCheck: string, str: string[], caseInsensitive: boolean = true): boolean {
    if (this.isEmpty(stringToCheck) || !str || str.length < 1) {
      return false;
    }

    if (caseInsensitive) {
      stringToCheck = stringToCheck.toLocaleLowerCase();
    }

    const match = str.find((text) => {
      if (caseInsensitive) {
        text = text.toLocaleLowerCase();
      }
      return stringToCheck.indexOf(text) !== -1;
    });
    return match != null;
  }

  public static getLineCount(str: string): number {
    if (this.isEmpty(str)) {
      return 0;
    }
    const s: string = this.rtrim(str);
    return StringUtils.getNumLines(s);
  }

  public static rtrim(s: string): string {
    if (this.isEmpty(s)) {
      return null;
    }
    let i: number;
    for (i = s.length - 1; i >= 0 && this.isWhitespace(s.charAt(i)); i--) {}
    return s.substring(0, i + 1);
  }

  public static padWithZeros(i: number, width: number): string {
    let result: string = '' + i;
    const isNegative: boolean = result.charAt(0) === '-';
    if (isNegative) {
      result = result.substr(1);
    }
    while (result.length < width) {
      result = '0' + result;
    }
    return isNegative ? '-' + result : result;
  }

  static join(data: any[], textFunction: Function, delimiter: string): string {
    if (!data || data.length === 0) {
      return '';
    }

    return data.map((d) => textFunction(d)).join(delimiter);
  }

  public static createUuid(): string {
    if (crypto?.randomUUID) {
      return crypto.randomUUID();
    } else {
      return Math.round(Math.random() * 0xffffffff).toString(16).padStart(8, '0')
        + "-" + Math.round(Math.random() * 0xffff).toString(16).padStart(4, '0')
        + "-" + Math.round(Math.random() * 0xffff).toString(16).padStart(4, '0')
        + "-" + Math.round(Math.random() * 0xffff).toString(16).padStart(4, '0')
        + "-" + Math.round(Math.random() * 0xffffff).toString(16).padStart(6, '0')
        + Math.round(Math.random() * 0xffffff).toString(16).padStart(6, '0');
    }
  }

  public static truncate(str: Nullable<string>, maxLength: number): string {
    if (StringUtils.isEmpty(str)) {
      return '';
    }
    return str.length > maxLength ? str.substring(0, maxLength) : str;
  }

  public static replaceByRegex(data: string, replacements: Map<RegExp, string>) {
    let result = data;
    for (const [key, value] of replacements) {
      result = result.replace(key, value);
    }
    return result;
  }
}
